/**
 * 虎鸡虫棒棋棋盘组件
 * 7×7棋盘，包含对角线和角落交叉线
 * 传统中国风格UI设计
 */

import React, { useState } from 'react';
import { TigerChickenGameState, Position, TigerChickenPiece, TigerChickenPieceType } from '../types/game';
import { TIGER_CHICKEN_PIECE_SYMBOLS, TIGER_CHICKEN_PIECE_EMOJIS, TIGER_CHICKEN_PIECE_NAMES } from '../types/game';
import { cn } from '../lib/utils';

interface TigerChickenBoardProps {
  gameState: TigerChickenGameState;
  onCellClick: (position: Position) => void;
  disabled?: boolean;
  gameMode?: 'pvp' | 'pvc';
}

const TigerChickenBoard: React.FC<TigerChickenBoardProps> = ({
  gameState,
  onCellClick,
  disabled = false,
  gameMode = 'pvp'
}) => {
  const board = gameState.board;

  // 放大状态管理
  const [isZoomed, setIsZoomed] = useState(false);

  // 响应式棋盘尺寸配置
  const getBoardSize = () => {
    // 获取屏幕宽度，默认为移动端竖屏
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
    const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 667;
    const isLandscape = screenWidth > screenHeight;

    // 根据屏幕尺寸动态调整
    if (isZoomed) {
      // 放大模式
      if (screenWidth >= 1024) { // 桌面
        return { width: 630, height: 630, margin: 45, cellSize: 90 };
      } else if (screenWidth >= 768) { // 平板
        return { width: 525, height: 525, margin: 37.5, cellSize: 75 };
      } else { // 手机
        const availableWidth = screenWidth - 40; // 留出边距
        const calculatedWidth = Math.min(availableWidth, 420);
        const scale = calculatedWidth / 420;
        return {
          width: calculatedWidth,
          height: calculatedWidth,
          margin: 30 * scale,
          cellSize: 60 * scale
        };
      }
    } else {
      // 普通模式 - 移动端优化
      if (screenWidth >= 1024) { // 桌面
        return { width: 420, height: 420, margin: 30, cellSize: 60 };
      } else if (screenWidth >= 768) { // 平板横屏
        return { width: 350, height: 350, margin: 25, cellSize: 50 };
      } else { // 手机竖屏 - 优化尺寸
        const availableWidth = screenWidth - 32; // 留出边距
        const calculatedWidth = Math.min(availableWidth, 320);
        const scale = calculatedWidth / 420;
        return {
          width: calculatedWidth,
          height: calculatedWidth,
          margin: 30 * scale,
          cellSize: 60 * scale
        };
      }
    }
  };

  const { width: boardWidth, height: boardHeight, margin, cellSize } = getBoardSize();
  const lineWidth = isZoomed ? '3px' : '2px';

  const getPieceSize = (): string => {
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;

    if (isZoomed) {
      if (screenWidth >= 1024) return 'text-2xl sm:text-3xl lg:text-4xl';
      if (screenWidth >= 768) return 'text-xl sm:text-2xl';
      return 'text-lg sm:text-xl';
    } else {
      if (screenWidth >= 1024) return 'text-lg sm:text-xl lg:text-2xl';
      if (screenWidth >= 768) return 'text-base sm:text-lg';
      return 'text-sm sm:text-base';
    }
  };

  const getPieceStyle = (piece: TigerChickenPiece, isSelected: boolean): string => {
    const baseStyle = 'flex items-center justify-center rounded-full shadow-2xl transition-all duration-300 font-bold relative';
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;

    let sizeStyle;
    if (isZoomed) {
      if (screenWidth >= 1024) sizeStyle = 'w-16 h-16';
      else if (screenWidth >= 768) sizeStyle = 'w-14 h-14';
      else sizeStyle = 'w-12 h-12';
    } else {
      if (screenWidth >= 1024) sizeStyle = 'w-12 h-12';
      else if (screenWidth >= 768) sizeStyle = 'w-10 h-10';
      else sizeStyle = 'w-9 h-9';
    }

    if (piece.player === 'black') {
      // 黑方棋子 - 渐变黑底，立体效果
      return `${baseStyle} ${sizeStyle} bg-gradient-to-br from-gray-700 to-gray-900 text-white border-3 border-black`;
    } else {
      // 白方棋子 - 渐变白底，立体效果
      return `${baseStyle} ${sizeStyle} bg-gradient-to-br from-white to-gray-200 text-gray-800 border-3 border-gray-400`;
    }
  };

  const renderPiece = (piece: TigerChickenPiece | null, row: number, col: number) => {
    if (!piece) return null;

    const isSelected = gameState.selectedPiece?.row === row && gameState.selectedPiece?.col === col;
    const isLastMove = gameState.moves.length > 0 &&
      gameState.moves[gameState.moves.length - 1].to.row === row &&
      gameState.moves[gameState.moves.length - 1].to.col === col;
    const isCurrentPlayerPiece = piece.player === gameState.currentPlayer;

    return (
      <div className={cn(
        getPieceStyle(piece, isSelected),
        {
          'scale-110 shadow-3xl border-4 border-yellow-400 ring-4 ring-yellow-300 ring-opacity-90': isSelected,
          'ring-2 ring-orange-400 ring-opacity-90 ring-offset-2': isLastMove && !isSelected,
          'opacity-95': !isCurrentPlayerPiece && !isSelected,
          'hover:scale-105': !disabled && isCurrentPlayerPiece
        }
      )}>
        {/* 内部装饰圆环 */}
        <div className={cn(
          "absolute inset-1 rounded-full border opacity-20",
          piece.player === 'black' ? 'border-white' : 'border-gray-600'
        )}></div>

        {/* 棋子文字 */}
        <div className={cn(
          getPieceSize(),
          'font-serif leading-none relative z-10 select-none',
          piece.player === 'black' ? 'text-white drop-shadow-sm' : 'text-gray-800 drop-shadow-sm'
        )}>
          {TIGER_CHICKEN_PIECE_SYMBOLS[piece.type][piece.player === 'black' ? 'black' : 'white']}
        </div>

        {/* 立体高光效果 */}
        <div className="absolute top-1 left-1 w-3 h-3 bg-white rounded-full opacity-50 blur-sm"></div>

        {/* 棋子类型标记 */}
        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center text-xs text-white font-bold">
          {TIGER_CHICKEN_PIECE_EMOJIS[piece.type][piece.player === 'black' ? 'black' : 'white']}
        </div>
      </div>
    );
  };

  // 检查位置是否是大本营
  const isHomePosition = (row: number, col: number, player: 'black' | 'white'): boolean => {
    if (player === 'black') {
      return row === 6 && col === 3; // 黑方大本营在底边中点
    } else {
      return row === 0 && col === 3; // 白方大本营在顶边中点
    }
  };

  // 获取最后一步的移动标记位置
  const getLastMovePosition = (): Position | null => {
    if (gameState.moves.length === 0) return null;
    const lastMove = gameState.moves[gameState.moves.length - 1];
    return lastMove.to;
  };

  const lastMovePos = getLastMovePosition();

  return (
    <div className={cn(
      "flex flex-col items-center space-y-4 px-2 sm:px-4",
      isZoomed ? "py-6" : "py-4"
    )}>
      {/* 放大模式提示 */}
      {isZoomed && (
        <div className="text-center text-sm text-green-600 font-medium animate-pulse">
          🔍 放大模式已启用 - 适合老人下棋
        </div>
      )}

      {/* 分数和状态显示 */}
      <div className="flex items-center justify-center space-x-8 bg-white rounded-lg shadow-lg px-6 py-4">
        <div className={`flex items-center space-x-3 ${gameState.currentPlayer === 'black' ? 'scale-110 transition-transform' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-700 to-black shadow-lg border-2 border-gray-900"></div>
          <div className="text-sm font-bold text-gray-800">
            黑方
          </div>
        </div>

        <div className="text-gray-400 text-lg">VS</div>

        <div className={`flex items-center space-x-3 ${gameState.currentPlayer === 'white' ? 'scale-110 transition-transform' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-white to-gray-200 shadow-lg border-2 border-gray-400"></div>
          <div className="text-sm font-bold text-gray-800">
            白方
          </div>
        </div>

        {/* 放大控制按钮 */}
        <button
          onClick={() => setIsZoomed(!isZoomed)}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 border-2 border-green-700"
          title={isZoomed ? "缩小棋盘" : "放大棋盘"}
        >
          <span className="text-xl font-bold">
            {isZoomed ? '🔍' : '🔎'}
          </span>
        </button>
      </div>

      {/* 虎鸡虫棒棋棋盘 */}
      <div className="relative bg-amber-200 p-6 rounded-xl shadow-2xl border-4 border-amber-900">
        {/* 棋盘容器 */}
        <div className={`relative bg-amber-50 rounded-lg shadow-inner transition-all duration-300`} style={{
          width: `${boardWidth}px`,
          height: `${boardHeight}px`
        }}>
          {/* 棋盘边框 */}
          <div className="absolute inset-2 border-4 border-amber-900 rounded pointer-events-none" style={{zIndex: 2}}></div>

          {/* 使用SVG绘制所有线条，统一管理 */}
          <svg
            className="absolute pointer-events-none"
            style={{
              top: `${margin}px`,
              left: `${margin}px`,
              width: `${6 * cellSize}px`,
              height: `${6 * cellSize}px`,
              zIndex: 5
            }}
          >
            {/* 横线 - 7条 */}
            {Array.from({ length: 7 }).map((_, rowIndex) => (
              <line
                key={`h-${rowIndex}`}
                x1="0"
                y1={`${rowIndex * cellSize}`}
                x2={`${6 * cellSize}`}
                y2={`${rowIndex * cellSize}`}
                stroke="#2c3e50"
                strokeWidth={isZoomed ? "2.5" : "2"}
                strokeLinecap="round"
              />
            ))}

            {/* 竖线 - 7条 */}
            {Array.from({ length: 7 }).map((_, colIndex) => (
              <line
                key={`v-${colIndex}`}
                x1={`${colIndex * cellSize}`}
                y1="0"
                x2={`${colIndex * cellSize}`}
                y2={`${6 * cellSize}`}
                stroke="#2c3e50"
                strokeWidth={isZoomed ? "2.5" : "2"}
                strokeLinecap="round"
              />
            ))}

            {/* 主对角线 (左上到右下) - 特殊样式 */}
            <line
              x1="0"
              y1="0"
              x2={`${6 * cellSize}`}
              y2={`${6 * cellSize}`}
              stroke="#c0392b"
              strokeWidth={isZoomed ? "3" : "2.5"}
              strokeLinecap="round"
              strokeDasharray={isZoomed ? "8,4" : "6,3"}
              opacity="0.8"
            />

            {/* 副对角线 (右上到左下) - 特殊样式 */}
            <line
              x1={`${6 * cellSize}`}
              y1="0"
              x2="0"
              y2={`${6 * cellSize}`}
              stroke="#c0392b"
              strokeWidth={isZoomed ? "3" : "2.5"}
              strokeLinecap="round"
              strokeDasharray={isZoomed ? "8,4" : "6,3"}
              opacity="0.8"
            />

            {/* 左上角到中心下方对角线 (4,0) -> (6,2) */}
            <line
              x1={`${4 * cellSize}`}
              y1={`${0 * cellSize}`}
              x2={`${6 * cellSize}`}
              y2={`${2 * cellSize}`}
              stroke="#c0392b"
              strokeWidth={isZoomed ? "3" : "2.5"}
              strokeLinecap="round"
              strokeDasharray={isZoomed ? "6,3" : "4,2"}
              opacity="0.6"
            />

            {/* 左下角到中心上方对角线 (2,0) -> (0,2) */}
            <line
              x1={`${2 * cellSize}`}
              y1={`${0 * cellSize}`}
              x2={`${0 * cellSize}`}
              y2={`${2 * cellSize}`}
              stroke="#c0392b"
              strokeWidth={isZoomed ? "3" : "2.5"}
              strokeLinecap="round"
              strokeDasharray={isZoomed ? "6,3" : "4,2"}
              opacity="0.6"
            />

            {/* 右上角到中心下方对角线 (2,6) -> (0,4) */}
            <line
              x1={`${2 * cellSize}`}
              y1={`${6 * cellSize}`}
              x2={`${0 * cellSize}`}
              y2={`${4 * cellSize}`}
              stroke="#c0392b"
              strokeWidth={isZoomed ? "3" : "2.5"}
              strokeLinecap="round"
              strokeDasharray={isZoomed ? "6,3" : "4,2"}
              opacity="0.6"
            />

            {/* 右下角到中心上方对角线 (4,6) -> (6,4) */}
            <line
              x1={`${4 * cellSize}`}
              y1={`${6 * cellSize}`}
              x2={`${6 * cellSize}`}
              y2={`${4 * cellSize}`}
              stroke="#c0392b"
              strokeWidth={isZoomed ? "3" : "2.5"}
              strokeLinecap="round"
              strokeDasharray={isZoomed ? "6,3" : "4,2"}
              opacity="0.6"
            />
          </svg>

          {/* 棋盘交叉点圆点装饰 */}
          {Array.from({ length: 7 }).map((_, rowIndex) =>
            Array.from({ length: 7 }).map((_, colIndex) => (
              // 只在重要位置添加圆点：四个角和中心点
              ((rowIndex === 0 || rowIndex === 6) && (colIndex === 0 || colIndex === 6)) ||
              (rowIndex === 3 && colIndex === 3) ? (
                <div
                  key={`dot-${rowIndex}-${colIndex}`}
                  className="absolute bg-amber-700 rounded-full shadow-sm"
                  style={{
                    top: `${margin + rowIndex * cellSize - 3}px`,
                    left: `${margin + colIndex * cellSize - 3}px`,
                    width: '6px',
                    height: '6px',
                    zIndex: 6
                  }}
                ></div>
              ) : null
            ))
          )}

          {/* 大本营标记 */}
          {/* 黑方大本营 (底边中点) */}
          <div
            className="absolute bg-gradient-to-br from-red-200 to-red-300 border-3 border-red-600 rounded-full flex items-center justify-center shadow-lg"
            style={{
              top: `${margin + 6 * cellSize - cellSize/2 + 2}px`,
              left: `${margin + 3 * cellSize - cellSize/2 + 2}px`,
              width: `${cellSize - 4}px`,
              height: `${cellSize - 4}px`,
              zIndex: 30
            }}
          >
            <div className="text-lg font-bold text-red-800">🏰</div>
          </div>

          {/* 白方大本营 (顶边中点) */}
          <div
            className="absolute bg-gradient-to-br from-blue-200 to-blue-300 border-3 border-blue-600 rounded-full flex items-center justify-center shadow-lg"
            style={{
              top: `${margin - cellSize/2 + 2}px`,
              left: `${margin + 3 * cellSize - cellSize/2 + 2}px`,
              width: `${cellSize - 4}px`,
              height: `${cellSize - 4}px`,
              zIndex: 30
            }}
          >
            <div className="text-lg font-bold text-blue-800">🏛️</div>
          </div>

          {/* 棋盘格子背景 */}
          {Array.from({ length: 7 }).map((_, rowIndex) =>
            Array.from({ length: 7 }).map((_, colIndex) => {
              // 检查是否是大本营周边的位置
              const isNearBlackHome = (rowIndex === 6 && (colIndex === 2 || colIndex === 4)) || (rowIndex === 5 && colIndex === 3);
              const isNearWhiteHome = (rowIndex === 0 && (colIndex === 2 || colIndex === 4)) || (rowIndex === 1 && colIndex === 3);

              let bgColor = "";
              if ((rowIndex + colIndex) % 2 === 0) {
                bgColor = "bg-amber-100 bg-opacity-30";
              } else {
                bgColor = "bg-amber-200 bg-opacity-20";
              }

              // 为大本营周边位置添加加深背景
              if (isNearBlackHome) {
                bgColor = "bg-red-300 bg-opacity-30"; // 红色调加深
              } else if (isNearWhiteHome) {
                bgColor = "bg-blue-300 bg-opacity-30"; // 蓝色调加深
              }

              return (
                <div
                  key={`bg-${rowIndex}-${colIndex}`}
                  className={cn(
                    "absolute rounded-full transition-all duration-200",
                    bgColor
                  )}
                  style={{
                    top: `${margin + rowIndex * cellSize - cellSize/2 + 2}px`,
                    left: `${margin + colIndex * cellSize - cellSize/2 + 2}px`,
                    width: `${cellSize - 4}px`,
                    height: `${cellSize - 4}px`,
                    zIndex: 1
                  }}
                ></div>
              );
            })
          )}

          {/* 所有交叉点的点击区域 */}
          {board.map((row, rowIndex) =>
            row.map((cell, colIndex) => (
              <div
                key={`click-${rowIndex}-${colIndex}`}
                className={cn(
                  "absolute flex items-center justify-center cursor-pointer transition-all duration-200",
                  {
                    "cursor-not-allowed opacity-100": disabled
                  }
                )}
                style={{
                  top: `${margin + rowIndex * cellSize - cellSize/2}px`,
                  left: `${margin + colIndex * cellSize - cellSize/2}px`,
                  width: `${cellSize}px`,
                  height: `${cellSize}px`,
                  zIndex: 10
                }}
                onClick={() => {
                  if (disabled) return;
                  onCellClick({ row: rowIndex, col: colIndex });
                }}
              >
                {/* 棋子 */}
                {cell && renderPiece(cell, rowIndex, colIndex)}

                {/* 选中状态 */}
                {gameState.selectedPiece?.row === rowIndex && gameState.selectedPiece?.col === colIndex && (
                  <div className="absolute inset-0 rounded-full border-4 border-yellow-400 bg-yellow-200 opacity-90 animate-pulse"></div>
                )}

                {/* 最后一步标记 */}
                {lastMovePos && lastMovePos.row === rowIndex && lastMovePos.col === colIndex && (
                  <div className="absolute -inset-2 rounded-full border-2 border-red-500 bg-red-500 opacity-20"></div>
                )}
              </div>
            ))
          )}

          {/* 可移动位置标记 */}
          {gameState.possibleMoves?.map((move, index) => (
            <div
              key={`move-${index}`}
              className="absolute flex items-center justify-center pointer-events-none"
              style={{
                top: `${margin + move.to.row * cellSize - cellSize/3}px`,
                left: `${margin + move.to.col * cellSize - cellSize/3}px`,
                width: `${cellSize * 0.66}px`,
                height: `${cellSize * 0.66}px`,
                zIndex: 15
              }}
            >
              <div className={cn(
                "rounded-full border-2 animate-pulse",
                board[move.to.row][move.to.col]
                  ? "bg-red-500 border-red-700 shadow-lg" // 可以吃子
                  : "bg-green-500 border-green-700 shadow-lg" // 空位移动
              )}
              style={{
                width: `${cellSize * 0.4}px`,
                height: `${cellSize * 0.4}px`
              }}></div>
            </div>
          ))}
        </div>
      </div>

      {/* 被吃掉的棋子显示 */}
      <div className="flex justify-between w-full max-w-2xl px-4">
        <div className="flex flex-col items-center">
          <div className="text-sm font-semibold text-gray-600 mb-2">黑方被吃</div>
          <div className="flex flex-wrap gap-1 max-w-xs">
            {gameState.capturedPieces.black.map((piece, index) => (
              <div key={index} className={cn(
                isZoomed ? "w-8 h-8 text-sm" : "w-6 h-6 text-xs",
                "rounded-full bg-gray-200 flex items-center justify-center border border-gray-400"
              )}>
                {TIGER_CHICKEN_PIECE_SYMBOLS[piece.type]['black']}
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-center">
          <div className="text-sm font-semibold text-gray-600 mb-2">白方被吃</div>
          <div className="flex flex-wrap gap-1 max-w-xs">
            {gameState.capturedPieces.white.map((piece, index) => (
              <div key={index} className={cn(
                isZoomed ? "w-8 h-8 text-sm" : "w-6 h-6 text-xs",
                "rounded-full bg-white flex items-center justify-center border border-gray-400"
              )}>
                {TIGER_CHICKEN_PIECE_SYMBOLS[piece.type]['white']}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 当前玩家指示 - 移动端 */}
      <div className="sm:hidden flex items-center space-x-4 bg-gradient-to-r from-amber-100 to-amber-200 rounded-lg px-6 py-3 border-2 border-amber-800">
        <span className="text-gray-700 font-medium font-serif">当前回合:</span>
        <div className="flex items-center space-x-3">
          <div className={cn(
            "w-8 h-8 rounded-full shadow-md transition-transform border-2",
            gameState.currentPlayer === 'black'
              ? 'bg-gray-900 border-black scale-110'
              : 'bg-white border-gray-400'
          )}>
            <div className="w-full h-full flex items-center justify-center font-bold text-sm">
              {gameState.currentPlayer === 'black' ? '黑' : '白'}
            </div>
          </div>
          <span className="font-bold text-gray-800 text-lg font-serif">
            {gameState.currentPlayer === 'black' ? '黑方' : '白方'}
          </span>
        </div>
      </div>

      {/* 游戏统计 - 移动端 */}
      <div className="sm:hidden flex justify-between w-full max-w-2xl px-4">
        {/* 黑方统计 */}
        <div className="text-sm text-gray-600 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg px-4 py-2 border-2 border-gray-700 font-serif flex-1 mr-2">
          <div className="text-center mb-1">
            <span className="text-gray-800 font-bold">黑方统计</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="flex flex-col items-center">
              <span className="text-xs text-gray-600">吃子</span>
              <span className="font-bold">{gameState.capturedPieces.white.length}</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-xs text-gray-600">步数</span>
              <span className="font-bold">{gameState.totalMoves}</span>
            </div>
          </div>
        </div>

        {/* 白方统计 */}
        <div className="text-sm text-gray-600 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg px-4 py-2 border-2 border-gray-400 font-serif flex-1 ml-2">
          <div className="text-center mb-1">
            <span className="text-gray-800 font-bold">白方统计</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="flex flex-col items-center">
              <span className="text-xs text-gray-600">吃子</span>
              <span className="font-bold">{gameState.capturedPieces.black.length}</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-xs text-gray-600">步数</span>
              <span className="font-bold">{gameState.totalMoves}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TigerChickenBoard;